import type { PlanetDef, PlanetId } from './types';

/**
 * All tunable constants for the activity. The PRP calls these out as expected
 * to change after child testing — keep them here, never inline.
 */
export const CONFIG = {
  /** ms after inactivity before prompted planet wiggles. */
  hintWiggleMs: 6000,
  /** ms after the wiggle before the correct silhouette pulses. */
  hintPulseExtraMs: 5000,
  /** ms for the ease-back to dock on a missed drop. */
  returnMs: 500,
  /** ms the orbit animation runs on completion. */
  orbitMs: 4000,
  /** px lift offset so a finger does not obscure the planet. */
  liftOffsetPx: 50,
  /** px added to each side of a silhouette bounding box as a placement margin. */
  targetMarginPx: 32,
  /** ms micro-animation (spin/glow) after a correct placement. */
  placeCelebrateMs: 900,
  /** Local storage key for mute preference. Non-personal. */
  muteStorageKey: 'ss-muted',
} as const;

/**
 * snapTolerance = clamp(72, min(viewportW, viewportH) * 0.16, 150)
 * Per PRP §Interaction Spec. Pure function — unit-tested across viewports.
 */
export function snapTolerance(viewportW: number, viewportH: number): number {
  const min = Math.min(viewportW, viewportH);
  const raw = min * 0.16;
  return Math.max(72, Math.min(raw, 150));
}

/**
 * Planets in astronomical order outward from the Sun. `orbitIndex` is also the
 * slot order on screen; the dock order is randomised separately at runtime.
 */
export const PLANETS: ReadonlyArray<PlanetDef> = [
  {
    id: 'earth',
    artPath: '/art/earth.svg',
    baseDiameter: 96,
    orbitIndex: 0,
    promptNarrationId: 'prompt-earth',
    successNarrationId: 'success-earth',
    label: 'Earth',
  },
  {
    id: 'mars',
    artPath: '/art/mars.svg',
    baseDiameter: 60,
    orbitIndex: 1,
    promptNarrationId: 'prompt-mars',
    successNarrationId: 'success-mars',
    label: 'Mars',
  },
  {
    id: 'jupiter',
    artPath: '/art/jupiter.svg',
    baseDiameter: 144,
    orbitIndex: 2,
    promptNarrationId: 'prompt-jupiter',
    successNarrationId: 'success-jupiter',
    label: 'Jupiter',
  },
  {
    id: 'saturn',
    artPath: '/art/saturn.svg',
    baseDiameter: 120,
    orbitIndex: 3,
    promptNarrationId: 'prompt-saturn',
    successNarrationId: 'success-saturn',
    label: 'Saturn',
  },
] as const;

export const PLANET_IDS: ReadonlyArray<PlanetId> = PLANETS.map((p) => p.id);

export const SUN_ART_PATH = '/art/sun.svg';

export const REQUIRED_ASSET_PATHS: ReadonlyArray<string> = [
  SUN_ART_PATH,
  ...PLANETS.map((p) => p.artPath),
  '/art/rocket-start.svg',
  '/art/rotate-device.svg',
  '/art/icon-speaker-on.svg',
  '/art/icon-speaker-off.svg',
  '/art/icon-narration.svg',
  '/art/icon-replay.svg',
  '/art/star.svg',
  '/art/star-counted.svg',
  '/art/icon-next-level.svg',
  '/art/icon-back-planets.svg',
  '/art/fuel-star.svg',
  '/art/fuel-rocket.svg',
  '/art/icon-next-fuel.svg',
  '/art/icon-back-stars.svg',
  '/source/narration.json',
  '/source/narration-count.json',
  '/source/narration-fuel.json',
  '/source/asset-manifest.json',
  '/manifest.webmanifest',
];

export const AUDIO_PATHS: ReadonlyArray<string> = [
  '/audio/intro.mp3',
  '/audio/prompt-earth.mp3',
  '/audio/prompt-mars.mp3',
  '/audio/prompt-jupiter.mp3',
  '/audio/prompt-saturn.mp3',
  '/audio/success-earth.mp3',
  '/audio/success-mars.mp3',
  '/audio/success-jupiter.mp3',
  '/audio/success-saturn.mp3',
  '/audio/completion.mp3',
  '/audio/chime-success.mp3',
  '/audio/count-intro.mp3',
  '/audio/count-1.mp3',
  '/audio/count-2.mp3',
  '/audio/count-3.mp3',
  '/audio/count-4.mp3',
  '/audio/count-5.mp3',
  '/audio/count-6.mp3',
  '/audio/count-7.mp3',
  '/audio/count-8.mp3',
  '/audio/count-9.mp3',
  '/audio/count-10.mp3',
  '/audio/count-tap-stars.mp3',
  '/audio/count-all-ten.mp3',
  '/audio/fuel-intro.mp3',
  '/audio/fuel-needs.mp3',
  '/audio/fuel-ready.mp3',
  '/audio/blast-off.mp3',
  '/audio/fuel-great.mp3',
];

export const CHIME_AUDIO_PATH = '/audio/chime-success.mp3';
